export type SeoProject = {
  id: string;
  shortId: string;
  organizationId: string;
  domain: string;
  name: string;
  siteUrl: string | null;
  ga4PropertyId: string | null;
  ga4PropertyName: string | null;
  wpSiteUrl: string | null;
  wpUsername: string | null;
  wpSeoPlugin: 'yoast' | 'rankmath' | 'none' | null;
  wpConnectedAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export enum AuditStatus {
  QUEUED = 'queued',
  RUNNING = 'running',
  COMPLETE = 'complete',
  FAILED = 'failed',
}

export type SeoAudit = {
  id: string;
  shortId: string;
  projectId: string;
  status: AuditStatus;
  healthScore: number | null;
  pagesCrawled: number;
  issuesFound: number;
  crawlDepth: number;
  startedAt: string | null;
  completedAt: string | null;
  errorMessage: string | null;
  createdBy: string;
  createdAt: string;
};

export type SeoAuditPage = {
  id: string;
  auditId: string;
  url: string;
  statusCode: number | null;
  title: string | null;
  metaDescription: string | null;
  wordCount: number | null;
  loadTimeMs: number | null;
  h1Count: number | null;
  internalLinks: number | null;
  externalLinks: number | null;
  imagesTotal: number | null;
  imagesNoAlt: number | null;
  createdAt: string;
};

export enum IssueCategory {
  TECHNICAL = 'technical',
  CONTENT = 'content',
  LINKS = 'links',
  PERFORMANCE = 'performance',
}

export enum IssueSeverity {
  CRITICAL = 'critical',
  WARNING = 'warning',
  INFO = 'info',
}

export const IssueSeverityLabel: { [s in IssueSeverity]: string } = {
  [IssueSeverity.CRITICAL]: 'Critical',
  [IssueSeverity.WARNING]: 'Warning',
  [IssueSeverity.INFO]: 'Info',
};

export const IssueCategoryLabel: { [c in IssueCategory]: string } = {
  [IssueCategory.TECHNICAL]: 'Technical',
  [IssueCategory.CONTENT]: 'Content',
  [IssueCategory.LINKS]: 'Links',
  [IssueCategory.PERFORMANCE]: 'Performance',
};

export type SeoAuditIssue = {
  id: string;
  auditId: string;
  pageId: string | null;
  category: IssueCategory;
  severity: IssueSeverity;
  issueType: string;
  description: string;
  affectedUrl: string | null;
  recommendation: string | null;
  isResolved: boolean;
  resolvedAt: string | null;
  resolvedBy: string | null;
  createdAt: string;
};

export type SeoPage = {
  id: string;
  projectId: string;
  url: string;
  path: string;
  title: string | null;
  metaDescription: string | null;
  wpPostId: number | null;
  wpPostType: string | null;
  source: 'sitemap' | 'wordpress' | 'gsc';
  automationEnabled: boolean;
  seoTrack: 'blog' | 'service' | null;
  contentUpdatedAt: string | null;
  lastSeenAt: string;
  createdAt: string;
};

export type SeoInternalLink = {
  id: string;
  projectId: string;
  auditId: string | null;
  sourceUrl: string;
  targetUrl: string;
  anchorText: string | null;
  createdAt: string;
};

export type SeoCopilotSession = {
  id: string;
  projectId: string;
  keywordId: string;
  targetPosition: number;
  status: 'active' | 'paused' | 'completed' | 'failed';
  currentPosition: number | null;
  startingPosition: number | null;
  bestPosition: number | null;
  totalCycles: number;
  maxCycles: number;
  lastCycleAt: string | null;
  nextCycleAt: string | null;
  completedAt: string | null;
  createdBy: string;
  createdAt: string;
  keyword?: SeoTrackedKeyword;
};

export type SeoCopilotCycle = {
  id: string;
  sessionId: string;
  cycleNumber: number;
  positionBefore: number | null;
  ctrBefore: number | null;
  clicksBefore: number | null;
  impressionsBefore: number | null;
  opportunitiesFound: number;
  selectedOpportunityType: string | null;
  selectedOpportunityTitle: string | null;
  diagnosisReasoning: string | null;
  actionTaken: string | null;
  actionDescription: string | null;
  appliedValue: string | null;
  positionAfter: number | null;
  impactStatus: string | null;
  status: string;
  errorMessage: string | null;
  createdAt: string;
};

export type SeoTrackedKeyword = {
  id: string;
  shortId: string;
  projectId: string;
  keyword: string;
  searchEngine: string;
  location: string;
  targetPageId: string | null;
  targetPage?: SeoPage | null;
  createdBy: string;
  createdAt: string;
  deletedAt: string | null;
  latestSnapshot?: SeoKeywordSnapshot | null;
};

export type SeoKeywordSnapshot = {
  id: string;
  keywordId: string;
  position: number | null;
  previousPosition: number | null;
  url: string | null;
  searchVolume: number | null;
  clicks: number | null;
  impressions: number | null;
  ctr: number | null;
  snapshotDate: string;
};

export enum ContentAnalysisStatus {
  QUEUED = 'queued',
  PROCESSING = 'processing',
  COMPLETE = 'complete',
  FAILED = 'failed',
}

export type ContentCheckResult = {
  value: string | number | null;
  score: number;
  issues: string[];
};

export type ContentAnalysisResults = {
  titleTag: ContentCheckResult;
  metaDescription: ContentCheckResult;
  headings: ContentCheckResult;
  keywordDensity: ContentCheckResult;
  wordCount: ContentCheckResult;
  readability: ContentCheckResult;
  internalLinks: ContentCheckResult;
  externalLinks: ContentCheckResult;
  imageAltText: ContentCheckResult;
};

export type SeoContentAnalysis = {
  id: string;
  shortId: string;
  projectId: string;
  url: string;
  targetKeyword: string;
  overallScore: number | null;
  status: ContentAnalysisStatus;
  results: ContentAnalysisResults | null;
  createdBy: string;
  createdAt: string;
};

export type SeoSerpAnalysis = {
  id: string;
  projectId: string;
  keywordId: string;
  keyword: string;
  status: string;
  serpResults: any;
  summary: any;
  errorMessage: string | null;
  createdBy: string;
  createdAt: string;
  expiresAt: string;
};

export type SeoContentOptimization = {
  id: string;
  projectId: string;
  keywordId: string;
  pageUrl: string;
  overallScore: number | null;
  scores: any;
  recommendations: any;
  status: string;
  createdBy: string;
  createdAt: string;
  expiresAt: string;
};

export type SeoInternalLinkSuggestion = {
  id: string;
  projectId: string;
  keywordId: string;
  targetPage: string;
  suggestions: any;
  status: string;
  createdBy: string;
  createdAt: string;
  expiresAt: string;
};

export type FixGuidance = {
  steps: string[];
  estimatedImpact: string;
  difficulty: 'easy' | 'medium' | 'hard';
  timeEstimate: string;
};

export type OpportunityStatus = 'open' | 'in_progress' | 'completed' | 'dismissed';

export type AiSuggestion = {
  type: string;
  label: string;
  value: string;
  reasoning: string;
};

export type SeoKeywordOpportunity = {
  id: string;
  projectId: string;
  keywordId: string;
  opportunityType: string;
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  metric: string;
  impactScore: number;
  aiFixGuidance: FixGuidance | null;
  aiSuggestions: AiSuggestion[] | null;
  detectedPosition: number | null;
  detectedCtr: number | null;
  detectedClicks: number | null;
  detectedImpressions: number | null;
  status: OpportunityStatus;
  isAiGenerated: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  expiresAt: string | null;
  actions?: SeoOpportunityAction[];
  snapshots?: SeoOpportunitySnapshot[];
};

export type SeoOpportunityAction = {
  id: string;
  opportunityId: string;
  actionType: string;
  description: string;
  notes: string | null;
  positionAtAction: number | null;
  ctrAtAction: number | null;
  clicksAtAction: number | null;
  impressionsAtAction: number | null;
  createdBy: string;
  createdAt: string;
};

export type SeoOpportunitySnapshot = {
  id: string;
  opportunityId: string;
  position: number | null;
  ctr: number | null;
  clicks: number | null;
  impressions: number | null;
  snapshotDate: string;
};

export type WpAppliedChange = {
  id: string;
  projectId: string;
  opportunityId: string | null;
  wpPostId: number;
  changeType: 'title_tag' | 'meta_description' | 'slug' | 'content';
  previousValue: string | null;
  newValue: string;
  pageUrl: string | null;
  keywordId: string | null;
  positionBefore: number | null;
  ctrBefore: number | null;
  clicksBefore: number | null;
  impressionsBefore: number | null;
  positionAfter: number | null;
  ctrAfter: number | null;
  clicksAfter: number | null;
  impressionsAfter: number | null;
  afterMeasuredAt: string | null;
  impactStatus: 'pending' | 'improved' | 'declined' | 'no_change' | 'insufficient_data';
  appliedBy: string;
  appliedAt: string;
};

export type PageImpactSummary = {
  totalChanges: number;
  latestChangeAt: string;
  impactStatus: 'pending' | 'improved' | 'declined' | 'no_change' | 'mixed' | 'insufficient_data';
  positionDelta: number | null;
  changes: WpAppliedChange[];
};

export type SeoTask = {
  id: string;
  projectId: string;
  keywordId: string | null;
  opportunityId: string | null;
  pageId: string | null;
  cycleId: string | null;
  agentId: string | null;
  source: 'manual' | 'agent' | 'copilot';
  title: string;
  description: string | null;
  taskType:
    | 'title_tag'
    | 'meta_description'
    | 'content'
    | 'schema'
    | 'internal_link'
    | 'technical'
    | 'backlink'
    | 'other';
  suggestedValue: string | null;
  qualityScore: number | null;
  status: 'todo' | 'in_progress' | 'done' | 'dismissed';
  priority: 'high' | 'medium' | 'low';
  appliedToWp: boolean;
  appliedAt: string | null;
  createdBy: string;
  assignedTo: string | null;
  dueDate: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

// ---------------------------------------------------------------------------
// SEO Automation — Agents, Changelog, Runs
// ---------------------------------------------------------------------------

export type SeoAgentTrack = 'blog' | 'service' | 'both';
export type SeoAgentMode = 'shadow' | 'auto' | 'disabled';

export type SeoAgent = {
  id: string;
  organizationId: string;
  projectId: string;
  name: string;
  description: string | null;
  track: SeoAgentTrack;
  mode: SeoAgentMode;
  scheduleCron: string | null;
  scheduleLabel: string | null;
  fieldScope: string[];
  llmModel: string;
  promptTemplate: string | null;
  monthlyTokenLimit: number | null;
  tokensUsedThisMonth: number;
  targetPageIds: string[];
  monthlyCostLimitUsd: number | null;
  costUsedThisMonthUsd: number;
  enabled: boolean;
  lastRunAt: string | null;
  lastRunStatus: string | null;
  totalRuns: number;
  totalChangesApplied: number;
  createdAt: string;
  updatedAt: string;
};

export type SeoAgentTemplate = {
  id: string;
  name: string;
  description: string | null;
  track: SeoAgentTrack;
  scheduleCron: string | null;
  scheduleLabel: string | null;
  fieldScope: string[];
  llmModel: string;
  promptTemplate: string | null;
  sortOrder: number;
};

export type SeoChangelog = {
  id: string;
  organizationId: string;
  projectId: string;
  pageId: string | null;
  taskId: string | null;
  agentId: string | null;
  pageUrl: string | null;
  fieldName: string;
  oldValue: any;
  newValue: any;
  appliedAt: string;
  appliedBy: 'agent' | 'manual' | 'copilot';
  shadow: boolean;
  revertedAt: string | null;
  revertedBy: string | null;
  revertOf: string | null;
  llmQualityScore: number | null;
  validationPassed: boolean | null;
  validationErrors: string[] | null;
  createdAt: string;
};

export type SeoAgentRun = {
  id: string;
  organizationId: string;
  agentId: string;
  startedAt: string;
  finishedAt: string | null;
  status: 'running' | 'completed' | 'error' | 'skipped';
  pagesScanned: number;
  pagesChanged: number;
  tasksCreated: number;
  changesApplied: number;
  changesShadowed: number;
  tokensUsed: number;
  costUsd: number;
  errorMessage: string | null;
  summary: string | null;
  createdAt: string;
};

export type SeoIndexRequest = {
  id: string;
  organizationId: string;
  projectId: string;
  pageUrl: string;
  requestedAt: string;
  source: 'agent' | 'revert' | 'manual';
  status: 'pending' | 'submitted' | 'error';
  errorMessage: string | null;
  createdAt: string;
};

// ---------------------------------------------------------------------------
// Ahrefs Integration
// ---------------------------------------------------------------------------

export type SeoAhrefsConnection = {
  id: string;
  organizationId: string;
  isActive: boolean;
  unitsUsedToday: number;
  unitsResetAt: string;
  connectedAt: string;
  updatedAt: string;
};

export type SeoAhrefsDomainSnapshot = {
  id: string;
  projectId: string;
  domainRating: number | null;
  ahrefsRank: number | null;
  backlinksLive: number | null;
  backlinksAll: number | null;
  refdomainsLive: number | null;
  refdomainsAll: number | null;
  dofollowLinks: number | null;
  nofollowLinks: number | null;
  snapshotDate: string;
  fetchedAt: string;
};

export type SeoAhrefsBacklink = {
  id: string;
  projectId: string;
  urlFrom: string;
  urlTo: string;
  domainFrom: string;
  anchor: string | null;
  domainRatingSource: number | null;
  isDofollow: boolean;
  isLost: boolean;
  firstSeen: string | null;
  lastSeen: string | null;
  httpCode: number | null;
  fetchedAt: string;
};

export type SeoAhrefsRefDomain = {
  id: string;
  projectId: string;
  domain: string;
  domainRating: number | null;
  backlinksCount: number;
  dofollowCount: number;
  firstSeen: string | null;
  isLost: boolean;
  fetchedAt: string;
};

export type SeoAhrefsCompetitor = {
  id: string;
  projectId: string;
  competitorDomain: string;
  commonKeywords: number;
  domainRating: number | null;
  organicTraffic: number | null;
  organicKeywords: number | null;
  fetchedAt: string;
};

export type SeoAhrefsCompetitorKeyword = {
  id: string;
  projectId: string;
  competitorDomain: string;
  keyword: string;
  position: number | null;
  volume: number | null;
  difficulty: number | null;
  traffic: number | null;
  url: string | null;
  fetchedAt: string;
};

export type SeoAhrefsKeywordCache = {
  id: string;
  keyword: string;
  country: string;
  difficulty: number | null;
  volume: number | null;
  cpc: number | null;
  searchIntent: string | null;
  parentTopic: string | null;
  fetchedAt: string;
  expiresAt: string;
};
